import React, {
  Suspense,
  lazy,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { Link } from "react-router-dom";
import { Heart } from "lucide-react";
import Nav from "../landing-pages/home-two/components/nav";
import { MTN_YELLOW } from "../../constants/Colors";
import { useHideEbanqoWidget } from "../../hooks/useHideEbanqoWidget";
import GameOverPanel from "../../components/games/GameOverPanel";
import HapticsToggle from "../../components/games/HapticsToggle";
import { HAPTIC, haptic, stopHaptics } from "../../lib/haptics";
import {
  endOfRunMessage,
  pickPraise,
  runWentWell,
} from "../../components/games/encouragement";
import {
  dayProgress,
  type DoseItem,
  type SlotState,
} from "../../components/games/dose/dose-config";
import {
  LIVES,
  buildTrayForDay,
  nextTablet,
  perfectDayBonus,
  pickTablet,
  GOOD_SCORE,
  pickTaunt,
  planDay,
  readBestScore,
  shouldSwapTablet,
  slotScore,
  writeBestScore,
  type DayPlan,
} from "../../components/games/dose/dose-rules";

/**
 * Dose Dash. A day passes; slots come due; catch each one with the tablet the
 * run is currently asking for. Miss it, or grab the wrong thing, and it costs
 * a life. Survive the day and the next one is shorter, its window narrower,
 * its tray busier.
 *
 * What it exercises is prospective memory and inhibition — remembering to act
 * at a future moment, and not grabbing whatever is nearest. The tablet is
 * swapped every third slot, so the target has to be updated mid-run rather
 * than just held.
 *
 * three.js is lazy-loaded. Rules and curve live in
 * components/games/dose/dose-rules; the scene holds no game state.
 *
 * Copy rule, as everywhere in here: this is a game. Generic tablets, no
 * dosing, no claim of a cognitive or medical benefit, and nothing that scolds
 * a food.
 */
const DoseDay = lazy(() => import("../../components/games/dose/DoseDay"));

/** State is derived on a timer rather than per frame: these are decisions
 *  about game state, not animation, and ten a second is finer than a thumb. */
const TICK_MS = 100;
/** Beat between the sun setting and the next day starting. */
const DAY_BREAK_MS = 1200;

type Phase = "idle" | "running" | "dayOver" | "over";

export default function DoseDashPage() {
  useHideEbanqoWidget("dose-dash");

  const [phase, setPhase] = useState<Phase>("idle");
  const [day, setDay] = useState(1);
  const [plan, setPlan] = useState<DayPlan>(() => planDay(1));
  const [startedAtMs, setStartedAtMs] = useState(() => performance.now());
  const [progress, setProgress] = useState(0);
  const [slotStates, setSlotStates] = useState<Record<string, SlotState>>({});
  const [tablet, setTablet] = useState<DoseItem>(() => pickTablet());
  const [tray, setTray] = useState<DoseItem[]>([]);
  const [lives, setLives] = useState(LIVES);
  const [score, setScore] = useState(0);
  const [best, setBest] = useState(0);
  const [daysSurvived, setDaysSurvived] = useState(0);
  const [flash, setFlash] = useState<string | null>(null);
  const [endMessage, setEndMessage] = useState("");
  /** The score as the run ends, read by the end-of-run message before
   *  writeBestScore moves the goalposts. */
  const scoreRef = useRef(0);
  /** The best as the run began. The panel's face and the end-of-run line both
   *  need it, and writeBestScore has already moved it by then. */
  const bestBeforeRun = useRef(0);
  /**
   * Pausing is allowed, but only when nothing is due.
   *
   * With no slot open there is no decision being made, so freezing the clock
   * costs the game nothing — it just lets someone answer the door. Freezing a
   * LIVE window would remove the whole challenge, so the button is refused
   * there (Ken asked whether pause defeats the purpose; that is the line).
   *
   * Backgrounding the app pauses regardless of what is open. That is not the
   * player dodging a window, it is a phone call — and before this, a hidden
   * tab kept ticking on the wall clock and quietly spent every life.
   */
  const [paused, setPaused] = useState(false);
  const pausedAtMs = useRef<number | null>(null);

  const openSlot = useRef<number | null>(null);
  const slotsResolved = useRef(0);
  /**
   * The authoritative slot state for the running day, mirrored into React
   * state for rendering.
   *
   * It lives in a ref because deciding a slot's fate has SIDE EFFECTS — a
   * life lost, a tablet swapped, a tray rebuilt — and a setState updater has
   * to be pure. Running those inside the updater cost two lives per missed
   * slot under StrictMode, which is how lives reached -1.
   */
  const slotStatesRef = useRef<Record<string, SlotState>>({});
  /** So game over fires once, whatever order the ticks land in. */
  const runOver = useRef(false);
  const timer = useRef<number | null>(null);
  const timeouts = useRef<number[]>([]);

  const later = useCallback((fn: () => void, ms: number) => {
    timeouts.current.push(window.setTimeout(fn, ms));
  }, []);
  const clearTimers = useCallback(() => {
    if (timer.current !== null) window.clearInterval(timer.current);
    timeouts.current.forEach(window.clearTimeout);
    timeouts.current = [];
  }, []);

  // Mirrors score, so the end-of-run message can read the final value
  // without waiting for a re-render.
  useEffect(() => {
    scoreRef.current = score;
  }, [score]);

  useEffect(() => {
    setBest(readBestScore());
    return clearTimers;
  }, [clearTimers]);

  const say = useCallback(
    (message: string, ms = 1100) => {
      setFlash(message);
      later(() => setFlash(null), ms);
    },
    [later],
  );

  /** Freezes the day. The clock is wall-clock based, so resuming shifts the
   *  day's start forward by however long the pause lasted. */
  const pause = useCallback(() => {
    setPaused((already) => {
      if (already) return already;
      pausedAtMs.current = performance.now();
      return true;
    });
  }, []);

  const resume = useCallback(() => {
    setPaused((wasPaused) => {
      if (!wasPaused) return wasPaused;
      const held = pausedAtMs.current;
      if (held !== null) {
        const heldFor = performance.now() - held;
        setStartedAtMs((start) => start + heldFor);
        pausedAtMs.current = null;
      }
      return false;
    });
  }, []);

  // A backgrounded tab used to keep spending lives: the interval is throttled
  // but never stops, and progress comes from the wall clock either way.
  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.hidden) pause();
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () =>
      document.removeEventListener("visibilitychange", onVisibilityChange);
  }, [pause]);

  const startDay = useCallback((dayNumber: number) => {
    const nextPlan = planDay(dayNumber);
    setDay(dayNumber);
    setPlan(nextPlan);
    slotStatesRef.current = {};
    setSlotStates({});
    setPaused(false);
    pausedAtMs.current = null;
    setTray([]);
    setProgress(0);
    openSlot.current = null;
    setStartedAtMs(performance.now());
    setPhase("running");
  }, []);

  const startRun = useCallback(() => {
    clearTimers();
    slotsResolved.current = 0;
    runOver.current = false;
    setScore(0);
    livesRef.current = LIVES;
    setLives(LIVES);
    setDaysSurvived(0);
    setTablet(pickTablet());
    startDay(1);
  }, [clearTimers, startDay]);

  /** Called when a slot is settled, however it was settled. Every third one
   *  swaps the tablet, announced so it is a difficulty and not a trick. */
  const afterSlotResolved = useCallback(() => {
    slotsResolved.current += 1;
    if (!shouldSwapTablet(slotsResolved.current)) return;

    setTablet((current) => {
      const swapped = nextTablet(current);
      haptic(HAPTIC.swap);
      say(`Tablet changed — now the ${swapped.label.toLowerCase()}`, 1600);
      return swapped;
    });
  }, [say]);

  const livesRef = useRef(LIVES);

  const loseALife = useCallback(
    (reason: string) => {
      haptic(HAPTIC.fail);
      say(reason);
      const left = Math.max(0, livesRef.current - 1);
      livesRef.current = left;
      setLives(left);

      if (left === 0 && !runOver.current) {
        runOver.current = true;
        stopHaptics();
        clearTimers();
        setPhase("over");
        // Read the old best HERE, not inside the updater: writeBestScore runs
          // in the same batch, and an updater reading it afterwards would
          // compare the run against itself, so beating your own best could
          // never earn praise.
          const previousBest = readBestScore();
          bestBeforeRun.current = previousBest;
          setEndMessage((previous) =>
            endOfRunMessage(
              {
                score: scoreRef.current,
                goodScore: GOOD_SCORE,
                previousBest,
              },
              { praise: pickPraise, taunt: pickTaunt },
              previous,
            ),
          );
        setScore((finalScore) => {
          writeBestScore(finalScore);
          setBest(readBestScore());
          return finalScore;
        });
      }
    },
    [clearTimers, say],
  );

  // The day's clock: opens slots, closes them, ends the day. All state
  // changes happen here in plain code — no side effects inside a setState
  // updater.
  useEffect(() => {
    if (phase !== "running" || paused) return;
    const dayPlan = plan;
    const started = startedAtMs;

    timer.current = window.setInterval(() => {
      const now = dayProgress(started, performance.now(), dayPlan.dayMs);
      setProgress(now);

      let changed = false;

      dayPlan.slotPoints.forEach((point, index) => {
        const key = String(index);
        const state = slotStatesRef.current[key] ?? "pending";
        if (state !== "pending" && state !== "open") return;

        const opensAt = point - dayPlan.window / 2;
        const closesAt = point + dayPlan.window / 2;

        if (now >= opensAt && now <= closesAt) {
          if (openSlot.current !== index) {
            openSlot.current = index;
            setTray(buildTrayForDay(tablet, dayPlan.trayCount));
            // The one moment the game asks you to notice something. Colour
            // alone carries it otherwise, which is no use to a thumb.
            haptic(HAPTIC.slotOpen);
          }
          if (state !== "open") {
            slotStatesRef.current[key] = "open";
            changed = true;
          }
          return;
        }

        if (now > closesAt) {
          if (openSlot.current === index) {
            openSlot.current = null;
            setTray([]);
          }
          slotStatesRef.current[key] = "missed";
          changed = true;
          loseALife("Missed it");
          afterSlotResolved();
        }
      });

      if (changed) setSlotStates({ ...slotStatesRef.current });

      // The day ends when the sun sets OR when every slot is settled —
      // waiting out an empty afternoon is dead time, not tension.
      const settled = dayPlan.slotPoints.every((_, index) => {
        const state = slotStatesRef.current[String(index)];
        return state === "taken" || state === "missed" || state === "wrong";
      });

      if ((now >= 1 || settled) && !runOver.current) {
        if (timer.current !== null) window.clearInterval(timer.current);
        setPhase((current) => (current === "running" ? "dayOver" : current));
      }
    }, TICK_MS);

    return () => {
      if (timer.current !== null) window.clearInterval(timer.current);
    };
  }, [phase, plan, startedAtMs, paused, tablet, loseALife, afterSlotResolved]);

  // Day over: pay the perfect-day bonus, then roll into the next day.
  useEffect(() => {
    if (phase !== "dayOver") return;

    const states = Object.values(slotStates);
    const caught = states.filter((state) => state === "taken").length;
    const clean = caught === plan.slotPoints.length;

    if (clean) {
      haptic(HAPTIC.success);
      const bonus = perfectDayBonus(day);
      setScore((current) => current + bonus);
      say(`Perfect day — +${bonus}`, 1400);
    }
    setDaysSurvived((current) => Math.max(current, day));

    later(() => {
      setPhase((current) => (current === "dayOver" ? "running" : current));
      startDay(day + 1);
    }, DAY_BREAK_MS);
  }, [phase, slotStates, plan, day, say, later, startDay]);

  const tapItem = (item: DoseItem) => {
    if (phase !== "running" || paused) return;
    const index = openSlot.current;
    if (index === null) {
      say("Nothing due yet", 800);
      return;
    }

    const key = String(index);
    const point = plan.slotPoints[index];
    const closesAt = point + plan.window / 2;
    // 1 when caught the instant it opened, 0 at the very end of the window.
    const promptness = (closesAt - progress) / plan.window;
    const correct = item.key === tablet.key;

    openSlot.current = null;
    setTray([]);
    slotStatesRef.current[key] = correct ? "taken" : "wrong";
    setSlotStates({ ...slotStatesRef.current });

    if (correct) {
      haptic(HAPTIC.tick);
      const gained = slotScore(day, promptness);
      setScore((current) => current + gained);
      say(`+${gained}`, 800);
    } else {
      loseALife(`That was the ${item.label.toLowerCase()}`);
    }
    afterSlotResolved();
  };

  const statusLine = (() => {
    switch (phase) {
      case "running":
        return openSlot.current !== null ? "Take it now" : "Wait for the slot";
      case "dayOver":
        return "Day over";
      case "over":
        return "Run over";
      default:
        return "Catch each slot with the tablet being asked for.";
    }
  })();

  return (
    <div className="flex min-h-screen w-full flex-col bg-neutral-950 text-white">
      <div className="text-black">
        <Nav />
      </div>

      <main
        className={`mx-auto flex w-full max-w-md flex-1 flex-col px-4 sm:max-w-xl md:max-w-2xl lg:max-w-3xl py-5 ${
          // Centre the game in whatever screen it gets: these boards are a
          // fixed share of the height, and on a tall phone the leftover used
          // to pile up under the last row. Not while the run-over panel is
          // up — that content is taller than the screen, and centring an
          // overflowing column puts its top out of reach.
          phase === "over" ? "" : "justify-center"
        }`}
      >
        <div className="flex items-center justify-between gap-3">
          <div>
            <h1 className="m-0 text-lg font-bold sm:text-2xl">Dose Dash</h1>
            <p className="m-0 text-[11px] text-white/40 sm:text-sm">Right thing, right time</p>
          </div>
          <div className="flex items-center gap-3 text-right">
            <div>
              <p className="m-0 text-[10px] uppercase tracking-wide text-white/40 sm:text-xs">
                Score
              </p>
              <p className="m-0 text-base font-bold tabular-nums sm:text-2xl">{score}</p>
            </div>
            <div className="flex gap-0.5" aria-label={`${lives} lives left`}>
              {Array.from({ length: LIVES }, (_, i) => (
                <Heart
                  key={i}
                  className="h-4 w-4"
                  strokeWidth={2.5}
                  style={{
                    color: i < lives ? MTN_YELLOW : "rgba(255,255,255,0.18)",
                    fill: i < lives ? MTN_YELLOW : "transparent",
                  }}
                  aria-hidden
                />
              ))}
            </div>
          </div>
        </div>

        <div className="mt-3 flex items-center justify-between gap-2 text-[11px] text-white/45 sm:text-sm">
          <span>
            {phase === "idle"
              ? "Ready"
              : `Day ${day} · ${plan.slotPoints.length} slots · ${(plan.dayMs / 1000).toFixed(0)}s`}
          </span>
          <div className="flex items-center gap-2">
            {phase === "running" && !paused && (
              <button
                type="button"
                onClick={pause}
                disabled={openSlot.current !== null}
                title={
                  openSlot.current !== null
                    ? "Not while a slot is open"
                    : "Pause the day"
                }
                className="rounded-md border border-white/20 px-2 py-0.5 text-[11px] font-semibold text-white/70 disabled:opacity-30 sm:px-3 sm:py-1 sm:text-sm"
              >
                Pause
              </button>
            )}
            <span>Best {best}</span>
          </div>
        </div>

        <div
          className={`relative mt-3 w-full overflow-hidden rounded-2xl bg-neutral-900 ring-1 ring-white/10 ${
            phase === "over" ? "h-[20vh] min-h-[140px]" : "h-[40vh] min-h-[200px] md:h-[50vh]"
          }`}
        >
          <Suspense
            fallback={
              <div className="flex h-full items-center justify-center text-xs text-white/40 sm:text-base">
                Loading the day…
              </div>
            }
          >
            <DoseDay
              startedAtMs={startedAtMs}
              dayMs={plan.dayMs}
              running={phase === "running" && !paused}
              slotPoints={plan.slotPoints}
              slotStates={slotStates}
            />
          </Suspense>

          {phase === "running" && paused && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-neutral-950/85 px-6 text-center">
              <p className="m-0 text-sm font-semibold">Paused</p>
              <p className="m-0 max-w-xs text-[11px] leading-relaxed text-white/50 sm:max-w-md sm:text-sm">
                The day is frozen where you left it.
              </p>
              <button
                type="button"
                onClick={resume}
                className="rounded-lg px-5 py-2.5 text-sm font-semibold text-black"
                style={{ backgroundColor: MTN_YELLOW }}
              >
                Resume
              </button>
            </div>
          )}

          {phase === "idle" && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-neutral-950/85 px-6 text-center">
              <p className="m-0 max-w-xs text-xs leading-relaxed text-white/60 sm:max-w-md sm:text-base">
                A day passes. When a slot lights up, take the tablet being
                asked for — and nothing else. It changes as you go.
              </p>
              <button
                type="button"
                onClick={startRun}
                className="rounded-lg px-5 py-2.5 text-sm font-semibold text-black"
                style={{ backgroundColor: MTN_YELLOW }}
              >
                Start
              </button>
            </div>
          )}
        </div>

        <div className="mt-2 h-1 w-full overflow-hidden rounded-full bg-white/10">
          <div
            className="h-full"
            style={{
              width: `${Math.round(progress * 100)}%`,
              backgroundColor: MTN_YELLOW,
            }}
          />
        </div>

        {/* What the run is asking for right now. Prominent, because it
            changes under you. */}
        <div className="mt-3 flex items-center justify-center gap-2 rounded-xl bg-white/5 px-3 py-2">
          <span className="text-[11px] uppercase tracking-wide text-white/40 sm:text-sm">
            Take the
          </span>
          <span
            className="h-4 w-4 rounded-full"
            style={{ backgroundColor: tablet.colour }}
            aria-hidden
          />
          <span className="text-xs font-semibold sm:text-lg">{tablet.label}</span>
        </div>

        <p className="mt-3 text-center text-xs text-white/60 sm:text-lg" aria-live="polite">
          {flash ?? statusLine}
        </p>

        {/* The reserved height keeps the layout still while the tray comes
            and goes mid-day. Once the run is over there is no tray to come
            back, so reserving for it only pushes the result down the page. */}
        <div
          className={
            phase === "running" || phase === "dayOver" ? "mt-3 min-h-[96px]" : ""
          }
        >
          {tray.length > 0 ? (
            <div
              className="grid gap-2"
              style={{
                gridTemplateColumns: `repeat(${Math.min(tray.length, 4)}, minmax(0, 1fr))`,
              }}
            >
              {tray.map((item) => (
                <button
                  key={item.key}
                  type="button"
                  onClick={() => tapItem(item)}
                  aria-label={item.label}
                  className="flex flex-col items-center gap-1.5 rounded-xl bg-white/10 px-2 py-2.5 text-[10px] font-semibold text-white/85 active:bg-white/25 sm:gap-2 sm:px-4 sm:py-4 sm:text-sm"
                >
                  <span
                    className={[
                      "h-7 w-7 shrink-0",
                      item.kind === "tablet" ? "rounded-full" : "rounded-md",
                    ].join(" ")}
                    style={{ backgroundColor: item.colour }}
                    aria-hidden
                  />
                  {item.label}
                </button>
              ))}
            </div>
          ) : phase === "running" ? (
            <p className="m-0 pt-7 text-center text-[11px] text-white/25 sm:text-sm">
              The tray appears when a slot is due.
            </p>
          ) : null}
        </div>

        {phase === "over" && (
          <GameOverPanel
            score={score}
            best={best}
            message={endMessage}
            wentWell={runWentWell({
              score,
              goodScore: GOOD_SCORE,
              previousBest: bestBeforeRun.current,
            })}
            peak={{
              value: daysSurvived,
              singular: "day",
              plural: "days",
              verb: "survived",
            }}
            gameSlug="dose-dash"
            rounds={daysSurvived}
            onPlayAgain={startRun}
          />
        )}

        <div className="mt-5 flex items-center justify-between gap-3 text-[11px] text-white/35 sm:text-sm">
          <Link to="/play" className="underline">
            All games
          </Link>
          <HapticsToggle />
          <Link to="/play/leaderboard?game=dose-dash" className="underline">
            Leaderboard
          </Link>
        </div>
      </main>
    </div>
  );
}
